<template>
  <div class="editor-module-container">
    <div class="editor-module-title">
      <div
        class="left-content"
        @click="clickRenameModule('edu', cvDetail.submodule_info.edus_title)"
      >
        <img v-if="moduleIcon" class="module-icon" :src="moduleIcon" alt="" />
        {{ cvDetail.submodule_info.edus_title }}
        <i class="iconfont iconedit-"></i>
        <div v-if="issueCount" class="module-issue-count">
          {{ issueCount }} 项待修复
        </div>
      </div>
      <div class="right-content">
        <i v-if="edus.length > 1" class="iconfont iconpaixu">
          <ExperienceSequence
            :list="edus"
            displayKey="university"
            @draggableChange="sequenceChange"
          />
        </i>
        <i
          class="iconfont iconshanchujianli"
          @click="handleClickRemoveModule"
        ></i>
      </div>
    </div>
    <div class="editor-form-list">
      <transition-group :name="displayAnimation ? 'list' : ''">
        <div
          v-for="(item, index) in edus"
          :key="item.id"
          :id="`experience${item.id}`"
          class="editor-form-content"
          @click="editItem(item, 'sameInfo')"
        >
          <div class="form-item-header">
            <div class="form-item-title">
              <div class="form-item-name">{{ item.university }}</div>
              <div class="form-item-edit-content">
                <i
                  class="iconfont iconshanchujianli"
                  @click.stop="removeExperience(item)"
                ></i>
                <i
                  class="iconfont iconchevron-down"
                  :class="{ active: editId === item.id }"
                  @click.stop="editItem(item, 'sameInfo')"
                ></i>
              </div>
            </div>
            <div
              v-if="item.major || item.start_at || item.end_at || item.present"
              class="form-item-content"
            >
              <div class="content-text">{{ item.major }}</div>
              <div class="content-text">
                {{ item.start_at | formatDate }} -
                <span v-if="item.present">至今</span>
                <span v-else>{{ item.end_at | formatDate }}</span>
              </div>
            </div>
          </div>
          <el-collapse-transition>
            <div
              v-show="editId === item.id"
              class="editor-module-content editor-base-form"
              @click.stop
            >
              <EditorAutoComplete
                :value="item.university"
                placeholder="请输入学校名称"
                clearable
                title="学校名称"
                value-key="name"
                :style="index !== 0 ? { width: '100%' } : {}"
                :remoteMethodCallback="fetchUniversityRemoteMethod"
                @clickPickerItem="universityChange($event, item)"
              />
              <div
                v-if="index === 0"
                class="school-logo-wrapper flex items-end"
              >
                <div class="school-logo-container flex items-center">
                  <div class="logo-content cursor-pointer" @click="showResumeSchoolModal">
                    <img
                      v-if="cvDetail.basic_info.school_logo_url"
                      :src="cvDetail.basic_info.school_logo_url"
                      class="w-full h-full cursor-pointer"
                      alt=""
                    />
                    <i
                      v-else
                      class="iconfont iconxinjianjianli cursor-pointer"
                    ></i>
                  </div>
                  <div class="flex flex-col operation">
                    <template v-if="cvDetail.basic_info.school_logo_url">
                      <span class="delete-btn" @click="deleteSchoolLogo">
                        删除
                      </span>
                      <span class="replace-btn" @click="showResumeSchoolModal">
                        更换
                      </span>
                    </template>
                    <span
                      v-else
                      class="upload-btn"
                      @click="showResumeSchoolModal"
                    >
                      上传校徽
                    </span>
                  </div>
                </div>
              </div>
              <div class="school-tag-container" style="width: 100%">
                <div class="school-tag-title">学校类型</div>
                <EditorLabelsPicker
                  v-model="item.show_tags"
                  :list="universityTags[cvDetail.basic_info.lang]"
                />
              </div>
              <EditorAutoComplete
                :value="item.major"
                placeholder="请输入专业名称"
                clearable
                title="专业"
                :remoteMethodCallback="fetchMajorRemoteMethod"
                @clickPickerItem="majorChange($event, item)"
              />
              <div style="display: flex; align-items: flex-end">
                <EditorSelect
                  label="学历"
                  v-model="item.degree"
                  :options="degreeList"
                  key-value="id"
                  :key-label="cvDetail.basic_info.lang"
                  clearable
                  placeholder="请选择"
                  style="width: 108px; margin-right: 16px"
                  @change="degreeChange($event, item)"
                />
                <EditorSelect
                  v-model="item.edu_type"
                  :options="eduTypes"
                  clearable
                  placeholder="请选择"
                  style="width: 108px"
                />
              </div>
              <EditorAutoComplete
                :value="item.uni_school"
                placeholder="请输入"
                clearable
                title="学院"
                :remoteMethodCallback="fetchUniSchoolRemoteMethod"
                @clickPickerItem="uniSchoolChange($event, item)"
              />
              <EditorCityPicker
                :value="item.city"
                @confirm="cityChange($event, item)"
              />
              <EditorDatePickerRange
                label="在读时间"
                :startDate="item.start_at"
                :endDate="item.end_at"
                clearable
                :is-present="!!item.present"
                :itemData="item"
                @startDateChange="startDateChange($event, item)"
                @endDateChange="endDateChange"
              />
              <EditorRichText
                ref="EditorRichTextRef"
                :description="item.description"
                :formData="item"
                :editId="editId"
                style="width: 100%"
                placeholder="可以列出成绩、荣誉奖项、相关课程等"
                @contentChange="contentChange($event, item)"
              />
              <EditorAdviceBlock
                :item="item"
                :module="'edus'"
                :suggestionList="returnCheckResultList(item.id, suggestionsModule)"
                :checkResult="checkResult"
                @singleExperienceCheck="clickSingleExperienceCheck"
                @saveExperience="saveExperience"
                @resolveIssue="resolveIssue"
                @clickIgnore="clickIgnore"
                @buyMember="buyMember"
                @useOptimizationContent="useOptimizationContent"
              />
              <div class="complete-button-wrapper">
                <CompleteButton
                  text="保存"
                  :disabled="experienceEmpty('edus', item.id, keys)"
                  @clickBtn="saveExperience(item.id, 'click')"
                />
              </div>
            </div>
          </el-collapse-transition>
        </div>
      </transition-group>
      <div
        v-if="displayAddBtn"
        class="editor-module-add"
        @click="clickAddExperience"
      >
        <i class="iconfont iconplus-circle"></i>
        <span>添加一段{{ cvDetail.submodule_info.edus_title }}</span>
      </div>
    </div>
    <RenameModuleModal
      ref="renameModalRef"
      @confirm="confirmModuleRename($event, 'edus')"
    />
    <ResumeSchoolModal ref="resumeSchoolModalRef" @buyMember="buyMember" />
  </div>
</template>
<script>
import { mapState } from "vuex";
import api from "@/api";
import { editorFormMixin } from "../../mixins/editor-form";
import moment from "moment";
import EditorInput from "../EditorInput/EditorInput.vue";
import EditorSelect from "../EditorSelect/EditorSelect.vue";
import EditorDatePickerRange from "../EditorDatePickerRange/EditorDatePickerRange.vue";
import EditorCityPicker from "../EditorCityPicker/EditorCityPicker.vue";
import EditorAutoComplete from "../EditorAutoComplete/EditorAutoComplete.vue";
import EditorRichText from "../EditorRichText/EditorRichText.vue";
import ExperienceSequence from "../ExperienceSequence/ExperienceSequence.vue";
import RenameModuleModal from "../RenameModuleModal/RenameModuleModal.vue";
import degree from "@/assets/json/degree";
import EditorLabelsPicker from "../EditorLabelsPicker/EditorLabelsPicker.vue";
import CompleteButton from "../CompleteButton/CompleteButton.vue";
import ResumeSchoolModal from "../ResumeSchoolModal/ResumeSchoolModal.vue";
import EditorAdviceBlock from '../EditorAdviceBlock/EditorAdviceBlock.vue';

const keys = [
  "university",
  "major",
  "degree",
  "edu_type",
  "uni_school",
  "city",
  "start_at",
  "end_at",
  "present",
];

const EDU_TYPES = [
  {
    label: "全日制",
    value: "全日制",
  },
  {
    label: "非全日制",
    value: "非全日制",
  },
  {
    label: "交换/交流",
    value: "交换/交流",
  },
];

const UNIVERSITY_TAGS = {
  en: ["985", "211", "Double 1st-Class", "QS Top100"],
  cn: ["985", "211", "双一流", "海外QS前100"],
};

export default {
  name: "EditorEduModule",
  mixins: [editorFormMixin],
  components: {
    EditorInput,
    EditorSelect,
    EditorDatePickerRange,
    EditorCityPicker,
    EditorAutoComplete,
    EditorRichText,
    EditorLabelsPicker,
    ExperienceSequence,
    RenameModuleModal,
    CompleteButton,
    ResumeSchoolModal,
    EditorAdviceBlock,
  },
  props: {
    editId: {
      type: [String, Number],
      default: "",
    },
    suggestionsModule: {
      type: Object,
      default: () => ({}),
    },
    saveLoading: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    editId(_, oldVal) {
      if (this.clickSaveBtn) return;
      if (oldVal === "edus-edit") {
        this.checkModuleEmpty("edus", oldVal, keys);
      } else if (oldVal) {
        this.saveExperience(oldVal);
      }
    },
  },
  data() {
    return {
      degreeList: degree,
      eduTypes: EDU_TYPES,
      universityTags: UNIVERSITY_TAGS,
      keys,
    };
  },
  computed: {
    ...mapState("editor", ["cvDetail", "edus"]),
    ...mapState("cvs", ["section_titles"]),
    displayAddBtn() {
      return this.edus.every((item) => item.id !== "edus-edit");
    },
  },
  filters: {
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("YYYY.MM");
    },
  },
  methods: {
    showResumeSchoolModal() {
      this.$refs.resumeSchoolModalRef.show();
    },
    async deleteSchoolLogo() {
      const confirm = await this.$wondercv.showConfirm({
        title: "温馨提示",
        content: `确定要删除吗？`,
        warningText: "校徽删除后无法恢复",
        confirmText: "确认",
        cancelText: "取消",
      });
      if (confirm) {
        await this.$store.dispatch("editor/updateCvDetail", {
          basic_info: {
            ...this.cvDetail.basic_info,
            school_logo_url: "",
            school_logo_id: null,
          },
          operation_type: "update_basic_info",
          skipCheck: true,
        });
      }
    },
    clickSingleExperienceCheck(item, type) {
      this.saveExperience(item.id, false, () => {
        this.singleExperienceCheck(item, type);
      });
    },
    saveExperience(id, operation, callback) {
      const payload = this.edus.find((item) => item.id === id);
      if (!payload) return;
      payload.lang = this.cvDetail.basic_info.lang;
      this.updateFormExperience(payload, "edus", operation, callback);
    },
    async resolveIssue(item, type) {
      const result = await this.clickRepairIssue(item, type);
      if (result === 'success') {
        this.saveExperience(item.id);
      }
    },
    sequenceChange() {
      this.experienceSequenceChange("edus");
    },
    async handleClickRemoveModule() {
      const confirm = await this.$wondercv.showConfirm({
        title: "温馨提示",
        content: `确定要删除${this.cvDetail.submodule_info.edus_title}吗？`,
        warningText: "模块删除后无法恢复",
        confirmText: "确认",
        cancelText: "取消",
      });
      if (confirm) {
        this.clickRemoveModule("edus");
      }
    },
    startDateChange(value, item) {
      item.start_at = value;
    },
    endDateChange(value, isPresent, item) {
      if (isPresent) {
        item.end_at = null;
        item.present = "至今";
      } else {
        item.end_at = value;
        item.present = "";
      }
    },
    cityChange(data, item) {
      if (typeof data === "string") {
        item.city = data;
        item.city_id = null;
      } else {
        item.city = data.name;
        item.city_id = data.id;
      }
    },
    majorChange(data, item) {
      if (typeof data === "string") {
        item.major = data;
        item.major_id = null;
      } else {
        item.major = data.name;
        item.major_id = data.id;
      }
    },
    universityChange(data, item) {
      if (typeof data === "string") {
        item.university = data;
        item.university_id = null;
      } else {
        item.university = data.name;
        item.university_id = data.id;
      }
    },
    uniSchoolChange(data, item) {
      item.uni_school = typeof data === "string" ? data : data.name;
    },
    degreeChange(id, item) {
      let {
        basic_info: { lang },
      } = this.cvDetail;
      const degreeItem = this.degreeList.find((item) => item.id === id);
      item.degree = degreeItem?.[lang] || null;
      item.degree_id = id || null;

      if (!item.degree) {
        item.edu_type = null;
      } else if (lang === "cn" && !item.edu_type) {
        item.edu_type = "全日制";
      }
    },
    contentChange(value, item) {
      item.description = value;
    },
    clickAddExperience() {
      this.$store.commit("editor/setModuleData", {
        type: "edus",
        info: [
          ...(this.$store.state.editor?.edus || []),
          {
            id: "edus-edit",
            university: "",
            degree: "",
            edu_type: "",
            start_at: "",
            end_at: "",
            present: "",
            description: "",
          },
        ],
      });
      this.$emit("editItem", "edus-edit");
    },
    async removeExperience(item) {
      try {
        const confirm = await this.$wondercv.showConfirm({
          title: "温馨提示",
          content: `确定要删除${item.university || ""}吗？`,
          warningText: "经历删除后无法恢复",
          confirmText: "确认",
          cancelText: "取消",
        });
        if (confirm) {
          this.clickRemoveExperience(item, "edus");
        }
      } catch (error) {
        console.log(error);
      }
    },
    editItem(item, sameInfo) {
      this.$emit("editItem", item.id, sameInfo);
    },
    async fetchUniversityRemoteMethod(query) {
      try {
        if (!query) return [];
        return await api.cvsEditor.universityAutoCompletions({
          keyword: query,
        });
      } catch (error) {
        console.log(error);
      }
    },
    async fetchMajorRemoteMethod(query) {
      try {
        if (!query) return [];
        return await api.cvsEditor.majorAutoCompletions({
          keyword: query,
        });
      } catch (error) {
        console.log(error);
      }
    },
    async fetchUniSchoolRemoteMethod(query) {
      try {
        if (!query) return [];
        return await api.cvsEditor.unischoolAutoCompletions({
          keyword: query,
        });
      } catch (error) {
        console.log(error);
      }
    },
    buyMember() {
      this.$emit("buyMember");
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/css/variable.scss";
@import "../../styles/editor.scss";
.school-logo-wrapper {
  width: 232px;
  overflow: hidden;
  .school-logo-container {
    height: fit-content;
    .logo-content {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      overflow: hidden;
      background-color: $upload-bg-color;
      width: 132px;
      height: 55px;
      .iconxinjianjianli {
        color: #6e7788;
        font-size: 24px;
      }
      img {
        width: 100px;
        object-fit: contain;
      }
    }
    .operation {
      margin-left: 18px;
      span {
        cursor: pointer;
        &.delete-btn {
          color: $delete-btn-color;
        }
        &.replace-btn {
          color: $replace-btn-color;
        }
        &.upload-btn {
          color: $theme-color;
        }
      }
    }
  }
}
</style>
